import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SessionStorageService } from '../services/session-storage.service';
import { AppNavigationService } from '../shared/page-layout/app-navigation.service';
import { findActiveMenuItem, normalizeRouterLink, normalizeUrl } from '../shared/page-layout/app-menu.utils';

export interface RouteUsageRecord {
    count: number;
    lastVisitedAt: number;
}

export type PageUsageMap = Record<string, RouteUsageRecord>;

const STORAGE_PREFIX = 'logilink-page-usage';
const MAX_ENTRIES = 60;
const EXCLUDED_PREFIXES = [
    '/dashboard',
    '/login',
    '/enterprise-subscription',
    '/posm/client-tracking',
    '/track'
];

@Injectable({
    providedIn: 'root'
})
export class PageUsageTrackerService {
    private readonly router = inject(Router);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly appNavigationService = inject(AppNavigationService);

    readonly usageSignal = signal<PageUsageMap>({});

    constructor() {
        this.loadCurrentUsage();
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(event => {
                this.handleNavigation(event.urlAfterRedirects || event.url);
            });
    }

    getTopVisitedRoutes(limit: number = 15): string[] {
        const usage = this.usageSignal();
        return Object.entries(usage)
            .sort((a, b) => (b[1].count - a[1].count) || (b[1].lastVisitedAt - a[1].lastVisitedAt))
            .slice(0, limit)
            .map(([route]) => route);
    }

    recordVisit(route: string): void {
        const canonical = this.resolveCanonicalRoute(route);
        if (!canonical || this.isExcluded(canonical)) {
            return;
        }

        const current = { ...this.usageSignal() };
        const existing = current[canonical] ?? { count: 0, lastVisitedAt: 0 };
        current[canonical] = {
            count: existing.count + 1,
            lastVisitedAt: Date.now()
        };

        const trimmed = this.trimToMax(current);
        this.usageSignal.set(trimmed);
        this.persistUsage(trimmed);
    }

    syncFromPreference(remoteUsage?: Record<string, unknown>): void {
        if (!remoteUsage || typeof remoteUsage !== 'object') {
            return;
        }

        const current = { ...this.usageSignal() };
        let changed = false;

        for (const [route, value] of Object.entries(remoteUsage)) {
            if (!value || typeof value !== 'object') {
                continue;
            }
            const record = value as Partial<RouteUsageRecord>;
            const count = typeof record.count === 'number' ? record.count : 0;
            const lastVisitedAt = typeof record.lastVisitedAt === 'number' ? record.lastVisitedAt : 0;

            if (count > 0) {
                const existing = current[route];
                if (!existing) {
                    current[route] = { count, lastVisitedAt };
                    changed = true;
                } else {
                    current[route] = {
                        count: Math.max(existing.count, count),
                        lastVisitedAt: Math.max(existing.lastVisitedAt, lastVisitedAt)
                    };
                    changed = true;
                }
            }
        }

        if (changed) {
            const trimmed = this.trimToMax(current);
            this.usageSignal.set(trimmed);
            this.persistUsage(trimmed);
        }
    }

    toPreferencePayload(): PageUsageMap {
        return this.usageSignal();
    }

    loadCurrentUsage(): void {
        const key = this.storageKey();
        if (!key) {
            this.usageSignal.set({});
            return;
        }

        try {
            const raw = window.localStorage.getItem(key);
            if (!raw) {
                this.usageSignal.set({});
                return;
            }
            const parsed = JSON.parse(raw) as PageUsageMap;
            this.usageSignal.set(this.trimToMax(parsed));
        } catch {
            this.usageSignal.set({});
        }
    }

    private handleNavigation(url: string): void {
        const user = this.sessionStorageService.getUser();
        const token = this.sessionStorageService.getAccessToken();
        if (!user || !token) {
            return;
        }
        this.recordVisit(url);
    }

    private resolveCanonicalRoute(url: string): string | null {
        const normalized = normalizeUrl(url);
        if (!normalized || this.isExcluded(normalized)) {
            return null;
        }

        const items = this.appNavigationService.menuItems();
        if (items.length) {
            const activeItem = findActiveMenuItem(items, normalized);
            const link = activeItem ? normalizeRouterLink(activeItem.routerLink) : null;
            if (link && !this.isExcluded(link)) {
                return link;
            }
        }

        return normalized;
    }

    private isExcluded(url: string): boolean {
        if (!url || url === '/' || url === '') {
            return true;
        }
        return EXCLUDED_PREFIXES.some(prefix => url === prefix || url.startsWith(`${prefix}/`));
    }

    private storageKey(): string | null {
        const companyId = this.sessionStorageService.getCompany()?.id;
        const customerId = this.sessionStorageService.getCustomer()?.id;
        if (!companyId || !customerId) {
            return null;
        }
        return `${STORAGE_PREFIX}:${companyId}:${customerId}`;
    }

    private persistUsage(map: PageUsageMap): void {
        const key = this.storageKey();
        if (!key) {
            return;
        }
        try {
            window.localStorage.setItem(key, JSON.stringify(map));
        } catch {
            // Ignore quota or serialization errors
        }
    }

    private trimToMax(map: PageUsageMap): PageUsageMap {
        const entries = Object.entries(map)
            .filter(([route, record]) => route && record && record.count > 0)
            .sort((a, b) => (b[1].count - a[1].count) || (b[1].lastVisitedAt - a[1].lastVisitedAt));

        const trimmed: PageUsageMap = {};
        for (const [route, record] of entries.slice(0, MAX_ENTRIES)) {
            trimmed[route] = record;
        }
        return trimmed;
    }
}

